import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { flightService, FlightSearchParams, MappedFlight } from '../services/flightService';
import { useAuth } from '../contexts/AuthContext';
import { generateQuotePDF } from '../utils/generateQuotePDF';
import styles from './QuotePage.module.css';
import AirportAutocomplete from '../components/AirportAutocomplete';
import FlightSearchLoader from '../components/FlightSearchLoader';
import { formatFlightTime } from '../utils/flightDateTime';

interface Airport { id: number; iata: string; name: string; city: string; state: string; country: string; }

// ─── tipos ───────────────────────────────────────────────────────────────────
type FlightOption = MappedFlight;

interface SearchResult {
  outboundFlights: FlightOption[];
  returnFlights?: FlightOption[];
  route: string;
}

interface PassengerForm {
  firstName: string;
  lastName: string;
  documentType: 'CPF' | 'PASSPORT';
  documentNumber: string;
  birthDay: string;
  birthMonth: string;
  birthYear: string;
  gender: 'male' | 'female';
}

const emptyPassenger = (): PassengerForm => ({
  firstName: '', lastName: '', documentType: 'CPF',
  documentNumber: '', birthDay: '', birthMonth: '', birthYear: '', gender: 'male',
});

// ─── helpers ─────────────────────────────────────────────────────────────────
// Horário de voo: wall-clock do aeroporto (sem conversão de fuso).
const fmtTime = (iso: string) => formatFlightTime(iso);

const fmtPrice = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ─── componente ──────────────────────────────────────────────────────────────
const QuotePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [params, setParams] = useState<FlightSearchParams>({
    iata_from: '', iata_to: '', date_outbound: '',
    adults: 1, children: 0, babies: 0, class_service: 3, is_loyalty: 0,
  });
  const [roundTrip, setRoundTrip] = useState(false);
  const [originAirport, setOriginAirport] = useState<Airport | null>(null);
  const [destAirport, setDestAirport] = useState<Airport | null>(null);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [outbound, setOutbound] = useState<FlightOption | null>(null);
  const [returnFlight, setReturnFlight] = useState<FlightOption | null>(null);

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [passengers, setPassengers] = useState<PassengerForm[]>([emptyPassenger()]);

  const [generatingLink, setGeneratingLink] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');
  const [linkError, setLinkError] = useState('');

  const totalPax = params.adults + params.children + params.babies;

  React.useEffect(() => {
    setPassengers(Array.from({ length: Math.max(1, totalPax) }, emptyPassenger));
  }, [totalPax]);

  const totalPrice = outbound
    ? (outbound.ourPrice + outbound.tax + (returnFlight ? returnFlight.ourPrice + returnFlight.tax : 0)) * totalPax
    : 0;

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!originAirport || !destAirport) { setSearchError('Selecione origem e destino'); return; }
    setSearching(true); setSearchError(''); setResult(null);
    setOutbound(null); setReturnFlight(null); setPaymentLink('');
    try {
      const data = await flightService.searchFlights(
        roundTrip ? params : { ...params, date_inbound: undefined }
      );
      setResult(data);
    } catch (err: any) {
      setSearchError(err.response?.data?.message || 'Erro ao buscar voos');
    } finally {
      setSearching(false);
    }
  };

  const updatePax = (i: number, field: keyof PassengerForm, value: string) =>
    setPassengers(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));

  const handleGenerateLink = async () => {
    if (!outbound || !email) return;
    setGeneratingLink(true); setLinkError('');
    try {
      const dto = buildDto();
      const res = await flightService.createPaymentLink(dto);
      setPaymentLink(res.paymentUrl);
    } catch (err: any) {
      setLinkError(err.response?.data?.message || 'Erro ao gerar link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const buildDto = () => {
    const rawPhone = phone.replace(/\D/g, '');
    const areaCode = rawPhone.length >= 10 ? rawPhone.slice(0, 2) : '31';
    const phoneNumber = rawPhone.length >= 10 ? rawPhone.slice(2, 11) : (rawPhone.slice(0, 9) || '000000000');
    return ({
    passengers: passengers.map(p => ({
      firstName: p.firstName, lastName: p.lastName,
      documentType: p.documentType, documentNumber: p.documentNumber,
      birthDay: p.birthDay, birthMonth: p.birthMonth, birthYear: p.birthYear,
      gender: p.gender, country: 'Brasil',
    })),
    contact: { email, confirmEmail: email, phoneNumber, areaCode },
    payment: { type: 'pix' },
    invoice: { type: 'individual' },
    flight: {
      tripType: returnFlight ? 'round_trip' : 'one_way',
      outboundFlightId: outbound!.uniqueId, outboundFareId: outbound!.uniqueId,
      returnFlightId: returnFlight?.uniqueId, returnFareId: returnFlight?.uniqueId,
      route: result!.route, date: params.date_outbound, returnDate: params.date_inbound,
      passengers: totalPax, outboundFlight: outbound!, returnFlight: returnFlight ?? undefined,
      departureTime: outbound!.departureTime, arrivalTime: outbound!.arrivalTime,
      departureLocation: outbound!.departureLocation, arrivalLocation: outbound!.arrivalLocation,
      airline: outbound!.airlineName, duration: outbound!.flightDuration,
      returnDepartureTime: returnFlight?.departureTime, returnArrivalTime: returnFlight?.arrivalTime,
    },
    pricing: {
      basePrice: (outbound!.ourPrice + (returnFlight?.ourPrice ?? 0)) * totalPax,
      taxes: (outbound!.tax + (returnFlight?.tax ?? 0)) * totalPax,
      total: totalPrice,
    },
    notes: `Cotação manual — operador: ${user?.username ?? ''}`,
  });
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Cotação Manual de Aéreo</h1>

      {/* ── abas ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button className={styles.btnPrimary} style={{ height: 40, padding: '0 20px' }}>
          🔍 Buscar via API
        </button>
        <button className={styles.btnSecondary} style={{ height: 40, padding: '0 20px' }}
          onClick={() => navigate('/flight-manual-checkout')}>
          ✏️ Entrada Manual
        </button>
      </div>

      {/* ── 1. Busca ── */}
      <form onSubmit={handleSearch} className={styles.card}>
        <h2 className={styles.sectionTitle}>1. Buscar Voos</h2>

        <div className={styles.formRow}>
          <AirportAutocomplete
            label="Origem"
            value={originAirport}
            onChange={a => { setOriginAirport(a); setParams(p => ({ ...p, iata_from: a?.iata ?? '' })); }}
          />
          <AirportAutocomplete
            label="Destino"
            value={destAirport}
            onChange={a => { setDestAirport(a); setParams(p => ({ ...p, iata_to: a?.iata ?? '' })); }}
          />
          <div className={styles.inputGroup}>
            <label>Data de Ida</label>
            <input type="date" value={params.date_outbound} required
              onChange={e => setParams(p => ({ ...p, date_outbound: e.target.value }))} />
          </div>
          {roundTrip && (
            <div className={styles.inputGroup}>
              <label>Data de Volta</label>
              <input type="date" value={params.date_inbound ?? ''}
                onChange={e => setParams(p => ({ ...p, date_inbound: e.target.value }))} />
            </div>
          )}
        </div>

        <div className={styles.formRow}>
          <label className={styles.checkRow}>
            <input type="checkbox" checked={roundTrip} onChange={e => setRoundTrip(e.target.checked)} />
            Ida e Volta
          </label>
        </div>

        <div className={styles.formRow}>
          {(['adults', 'children', 'babies'] as const).map(f => (
            <div key={f} className={styles.inputGroup} style={{ flex: '0 1 90px' }}>
              <label>{f === 'adults' ? 'Adultos' : f === 'children' ? 'Crianças' : 'Bebês'}</label>
              <input type="number" min={0} max={9} value={params[f]}
                onChange={e => setParams(p => ({ ...p, [f]: Number(e.target.value) }))} />
            </div>
          ))}
          <div className={styles.inputGroup} style={{ flex: '1 1 160px' }}>
            <label>Classe</label>
            <select value={params.class_service}
              onChange={e => setParams(p => ({ ...p, class_service: Number(e.target.value) }))}>
              <option value={1}>Econômica</option>
              <option value={2}>Premium Economy</option>
              <option value={3}>Todas as classes</option>
              <option value={4}>Primeira Classe</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button type="submit" className={styles.btnPrimary} disabled={searching}>
              {searching ? 'Buscando...' : '🔍 Buscar Voos'}
            </button>
          </div>
        </div>

        {searchError && <p className={styles.error}>{searchError}</p>}
      </form>

      {/* ── loading ── */}
      {searching && <FlightSearchLoader />}

      {/* ── 2. Resultados ── */}
      {!searching && result && (
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>2. Selecionar Voo</h2>

          <p className={styles.flightGroupTitle}>Voos de Ida</p>
          {result.outboundFlights?.map(f => (
            <FlightCard key={f.uniqueId} flight={f}
              selected={outbound?.uniqueId === f.uniqueId}
              onSelect={() => setOutbound(f)} />
          ))}

          {result.returnFlights && result.returnFlights.length > 0 && (
            <>
              <p className={styles.flightGroupTitle}>Voos de Volta</p>
              {result.returnFlights.map(f => (
                <FlightCard key={f.uniqueId} flight={f}
                  selected={returnFlight?.uniqueId === f.uniqueId}
                  onSelect={() => setReturnFlight(f)} />
              ))}
            </>
          )}
        </div>
      )}

      {/* ── 3. Dados do cliente ── */}
      {outbound && (
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>3. Dados do Cliente</h2>

          <div className={styles.formRow}>
            <div className={styles.inputGroup}>
              <label>Email</label>
              <input type="email" value={email} required onChange={e => setEmail(e.target.value)} />
            </div>
            <div className={styles.inputGroup}>
              <label>Telefone</label>
              <input value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
          </div>

          {passengers.map((p, i) => (
            <div key={i} className={styles.passengerCard}>
              <p className={styles.passengerTitle}>Passageiro {i + 1}</p>
              <div className={styles.formRow}>
                <div className={styles.inputGroup}>
                  <label>Nome</label>
                  <input value={p.firstName} onChange={e => updatePax(i, 'firstName', e.target.value)} />
                </div>
                <div className={styles.inputGroup}>
                  <label>Sobrenome</label>
                  <input value={p.lastName} onChange={e => updatePax(i, 'lastName', e.target.value)} />
                </div>
                <div className={styles.inputGroup} style={{ flex: '0 1 140px' }}>
                  <label>Gênero</label>
                  <select value={p.gender} onChange={e => updatePax(i, 'gender', e.target.value)}>
                    <option value="male">Masculino</option>
                    <option value="female">Feminino</option>
                  </select>
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.inputGroup} style={{ flex: '0 1 140px' }}>
                  <label>Documento</label>
                  <select value={p.documentType} onChange={e => updatePax(i, 'documentType', e.target.value)}>
                    <option value="CPF">CPF</option>
                    <option value="PASSPORT">Passaporte</option>
                  </select>
                </div>
                <div className={styles.inputGroup}>
                  <label>Número</label>
                  <input value={p.documentNumber} onChange={e => updatePax(i, 'documentNumber', e.target.value)} />
                </div>
                <div className={styles.inputGroup} style={{ flex: '0 1 auto' }}>
                  <label>Nascimento</label>
                  <div className={styles.birthRow}>
                    <div className={styles.inputGroup} style={{ flex: '0 1 70px' }}>
                      <label>Dia</label>
                      <input placeholder="DD" maxLength={2} value={p.birthDay} onChange={e => updatePax(i, 'birthDay', e.target.value)} />
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 1 70px' }}>
                      <label>Mês</label>
                      <input placeholder="MM" maxLength={2} value={p.birthMonth} onChange={e => updatePax(i, 'birthMonth', e.target.value)} />
                    </div>
                    <div className={styles.inputGroup} style={{ flex: '0 1 90px' }}>
                      <label>Ano</label>
                      <input placeholder="AAAA" maxLength={4} value={p.birthYear} onChange={e => updatePax(i, 'birthYear', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── 4. Resumo e ações ── */}
      {outbound && (
        <div className={styles.card}>
          <h2 className={styles.sectionTitle}>4. Resumo e Ações</h2>

          <p className={styles.summaryLine}>
            <strong>Rota:</strong> {outbound.departureLocation} → {outbound.arrivalLocation}
          </p>
          <p className={styles.summaryLine}>
            <strong>Ida:</strong> {outbound.airlineName} · {fmtTime(outbound.departureTime)} → {fmtTime(outbound.arrivalTime)} · {outbound.flightDuration}
          </p>
          {returnFlight && (
            <p className={styles.summaryLine}>
              <strong>Volta:</strong> {returnFlight.airlineName} · {fmtTime(returnFlight.departureTime)} → {fmtTime(returnFlight.arrivalTime)} · {returnFlight.flightDuration}
            </p>
          )}
          <p className={styles.summaryLine}>
            <strong>Passageiros:</strong> {params.adults} adulto(s){params.children > 0 ? `, ${params.children} criança(s)` : ''}{params.babies > 0 ? `, ${params.babies} bebê(s)` : ''}
          </p>
          <p className={styles.summaryTotal}>{fmtPrice(totalPrice)}</p>

          <div className={styles.btnRow}>
            <button className={styles.btnSecondary} onClick={() => generateQuotePDF(outbound, returnFlight, params, totalPrice, paymentLink || undefined)}>
              📄 Baixar PDF
            </button>
            <button className={styles.btnPrimary} onClick={handleGenerateLink}
              disabled={generatingLink || !email}>
              {generatingLink ? 'Gerando...' : '🔗 Gerar Link de Pagamento'}
            </button>
          </div>

          {linkError && <p className={styles.error}>{linkError}</p>}

          {paymentLink && (
            <div className={styles.linkBox}>
              <p className={styles.linkBoxTitle}>✅ Link gerado com sucesso!</p>
              <a href={paymentLink} target="_blank" rel="noreferrer" className={styles.linkUrl}>
                {paymentLink}
              </a>
              <div className={styles.btnRow} style={{ marginTop: 0 }}>
                <button className={styles.btnSecondary} onClick={() => navigator.clipboard.writeText(paymentLink)}>
                  📋 Copiar Link
                </button>
                <button className={styles.btnSecondary} onClick={() => generateQuotePDF(outbound, returnFlight, params, totalPrice, paymentLink)}>
                  📄 PDF com Link
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── FlightCard ───────────────────────────────────────────────────────────────
const FlightCard: React.FC<{ flight: FlightOption; selected: boolean; onSelect: () => void }> = ({ flight, selected, onSelect }) => (
  <div className={`${styles.flightCard} ${selected ? styles.flightCardSelected : ''}`} onClick={onSelect}>
    <div className={styles.flightCardLeft}>
      <span className={styles.flightAirline}>
        {flight.airlineName}
        {flight.cabin && <span className={styles.flightCabin}>{flight.cabin}</span>}
      </span>
      <span className={styles.flightInfo}>
        {flight.departureLocation} {fmtTime(flight.departureTime)}
        {' → '}
        {flight.arrivalLocation} {fmtTime(flight.arrivalTime)}
        {' · '}{flight.flightDuration}
        {flight.qntStop > 0 && ` · ${flight.qntStop} escala(s)`}
        {' · Voo '}{flight.flightNumber}
      </span>
    </div>
    <div>
      <span className={styles.flightPrice}>
        {fmtPrice(flight.ourPrice + flight.tax)}
        <span className={styles.flightPriceSub}>/pax</span>
      </span>
    </div>
  </div>
);

export default QuotePage;
